import { useEffect, useId, useRef, type FocusEvent, type MouseEvent } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { keyframes } from '@mui/material/styles';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useTranslation } from 'react-i18next';
import { primitiveColors, semanticColors } from '@/theme/tokens';
import type { CountyPreviewSource } from '@/hooks/useGuestAddress';
import type { ExactLocationState } from '@/hooks/useExactLocation';

const enter = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface CountyPreviewTipProps {
  /** "Travis County, TX". */
  place: string;
  /** Id for the instruction text, so the address field can be described by it. */
  bodyId: string;
  /** ``ip``: the county is a guess from the connection, and says so. */
  source: CountyPreviewSource;
  /** Shared with the notice under the ballot. */
  exact: ExactLocationState;
  onDismiss: () => void;
}

/**
 * On phones it sits at the tip's right edge, icon over a two-line label, so it
 * costs no row under the text. ``compact`` (desktop) is one line under the text.
 */
const ExactLocationButton = ({
  locating,
  compact,
  onClick,
  onBlur,
}: {
  locating: boolean;
  compact: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onBlur: (event: FocusEvent<HTMLButtonElement>) => void;
}) => {
  const { t } = useTranslation('ballot');
  return (
    <ButtonBase
      onClick={onClick}
      onBlur={onBlur}
      // Stays focusable while locating so keyboard users keep their place.
      aria-disabled={locating || undefined}
      disableRipple
      sx={{
        flexShrink: 0,
        alignSelf: compact ? 'flex-start' : 'center',
        flexDirection: compact ? 'row' : 'column',
        width: compact ? 'auto' : 104,
        minHeight: compact ? 32 : 44,
        mt: compact ? '8px' : 0,
        px: compact ? '10px' : '8px',
        py: compact ? '4px' : '6px',
        gap: compact ? '6px' : '2px',
        borderRadius: '8px',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.25,
        textAlign: compact ? 'start' : 'center',
        // Long single words (ru "местоположение") break inside the fixed width.
        hyphens: 'auto',
        overflowWrap: 'anywhere',
        color: semanticColors.textPrimary,
        border: `1px solid ${semanticColors.textPrimary}`,
        backgroundColor: primitiveColors.white,
        '&:hover': { backgroundColor: semanticColors.overlaySubtle },
        '&:focus-visible': {
          outline: `2px solid ${semanticColors.textPrimary}`,
          outlineOffset: '2px',
        },
        '&[aria-disabled="true"]': { cursor: 'progress' },
      }}
    >
      {locating ? (
        <CircularProgress
          size={16}
          thickness={5}
          sx={{ color: 'inherit', flexShrink: 0 }}
          aria-hidden
        />
      ) : (
        <MyLocationIcon sx={{ fontSize: 18, flexShrink: 0 }} aria-hidden />
      )}
      {t('countyPreview.useExactLocation')}
    </ButtonBase>
  );
};

/**
 * Sits above the ballot's address field while the ballot is a county sample
 * built from the device's location or an IP guess, so the voter knows it's
 * their county's and not their own, and where to fix that. The pointer aims at
 * the field below.
 */
const CountyPreviewTip = ({ place, bodyId, source, exact, onDismiss }: CountyPreviewTipProps) => {
  const { t } = useTranslation('ballot');
  const titleId = useId();
  const isDesktop = useMediaQuery(useTheme().breakpoints.up('md'));
  const isGuess = source === 'ip';
  // When the button unmounts (a denial, or an approximate fix), a keyboard
  // user's focus moves to the message that replaces it.
  const buttonFocusedRef = useRef(false);
  const messageRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!exact.offered && buttonFocusedRef.current) {
      buttonFocusedRef.current = false;
      messageRef.current?.focus();
    }
  }, [exact.offered]);
  const exactButton = exact.offered && (
    <ExactLocationButton
      locating={exact.locating}
      compact={isDesktop}
      onClick={(event) => {
        buttonFocusedRef.current = event.currentTarget.matches(':focus-visible');
        exact.locate('ballot_tip', buttonFocusedRef.current);
      }}
      onBlur={(event) => {
        // Only a move elsewhere; unmounting leaves no relatedTarget.
        if (event.relatedTarget) buttonFocusedRef.current = false;
      }}
    />
  );
  // A labelled section rather than a live region: it mounts already filled,
  // which live regions don't reliably announce. SampleBallotView announces it.
  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{
        position: 'relative',
        mx: 1,
        mt: 1,
        mb: '10px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: '8px', md: '12px' },
        p: { xs: '10px', md: '12px' },
        borderRadius: '12px',
        backgroundColor: primitiveColors.blue1,
        borderLeft: `3px solid ${semanticColors.warningBannerBorder}`,
        color: semanticColors.textPrimary,
        boxShadow: `0 4px 12px ${primitiveColors.rockSlate}1A`,
        animation: `${enter} 320ms cubic-bezier(0.2, 0, 0, 1) both`,
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 24,
          bottom: -7,
          width: 14,
          height: 14,
          backgroundColor: primitiveColors.blue1,
          transform: 'rotate(45deg)',
          borderRadius: '2px',
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          id={titleId}
          component="h2"
          sx={{ fontSize: { xs: 14, md: 15 }, fontWeight: 700, lineHeight: 1.3 }}
        >
          {t(isGuess ? 'countyPreview.guessTitle' : 'countyPreview.title', { place })}
        </Typography>
        <Typography
          id={bodyId}
          sx={{ mt: '2px', fontSize: { xs: 13, md: 14 }, lineHeight: { xs: 1.4, md: 1.45 } }}
        >
          {t(isGuess ? 'countyPreview.guessBody' : 'countyPreview.body')}
        </Typography>
        {/* SampleBallotView announces this; here it's for sighted voters and focus. */}
        <Typography
          ref={messageRef}
          component="span"
          tabIndex={-1}
          sx={{
            display: 'block',
            fontSize: 13,
            mt: exact.locating || exact.outcome ? '6px' : 0,
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.textPrimary}`,
              outlineOffset: '2px',
            },
          }}
        >
          {exact.locating
            ? t('countyPreview.locating')
            : exact.outcome === 'approximate'
              ? t('countyPreview.approximateLocation')
              : exact.outcome
                ? t('countyPreview.locationFailed')
                : ''}
        </Typography>
        {isDesktop && exactButton}
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '4px',
          // The close button's hit area reaches into the padding, keeping its
          // glyph aligned with the title.
          mt: '-6px',
          mr: '-6px',
        }}
      >
        <IconButton
          onClick={onDismiss}
          aria-label={t('countyPreview.dismiss')}
          size="small"
          sx={{
            width: 32,
            height: 32,
            color: semanticColors.textSecondary,
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.brandPrimaryHover}`,
              outlineOffset: '1px',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        {!isDesktop && exactButton && (
          <Box sx={{ mr: '6px', flex: 1, display: 'flex', alignItems: 'center' }}>
            {exactButton}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CountyPreviewTip;
